import { ArrowDown, ArrowUp, Check, X } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  RETRY_GAP_HOURS,
  servedOrder,
  type QueueRound,
} from "../content/readQueue";
import { feedbackBox, feedbackTitle } from "./feedbackStyles";
import { GameFrame } from "./GameFrame";

const RULES = [1, 2, 3, 4] as const;

/**
 * « Lis la file »: reorder 4 lead cards with Monter / Descendre (keyboard and touch friendly), then « Valider ».
 * The right order is the queue's own (servedOrder → queueOrderKey). One point per round right on the first check.
 */
export const OrderGame = ({
  rounds,
  onFinish,
  onQuit,
}: {
  rounds: readonly QueueRound[];
  onFinish: (correct: number, total: number) => void;
  onQuit: () => void;
}) => {
  const t = useTranslate();
  const [index, setIndex] = useState(0);
  const round = rounds[index];
  const [order, setOrder] = useState<number[]>(() =>
    round.cards.map((c) => c.lead.id),
  );
  const [checked, setChecked] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [focusTarget, setFocusTarget] = useState<string | null>(null);
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const nextRef = useRef<HTMLButtonElement>(null);
  const last = index === rounds.length - 1;
  const expected = servedOrder(round);
  const right = checked && order.every((id, i) => id === expected[i]);
  const cardOf = (id: number) => round.cards.find((c) => c.lead.id === id)!;

  // keep the focus on the moved card's button (or its other button at an edge)
  useEffect(() => {
    if (!focusTarget) return;
    const el = buttons.current.get(focusTarget);
    if (el && !el.disabled) el.focus();
    else {
      const [id, dir] = focusTarget.split(":");
      buttons.current.get(`${id}:${dir === "up" ? "down" : "up"}`)?.focus();
    }
    setFocusTarget(null);
  }, [focusTarget, order]);
  useEffect(() => {
    if (checked) nextRef.current?.focus();
  }, [checked]);

  const move = (id: number, delta: -1 | 1) => {
    setOrder((o) => {
      const i = o.indexOf(id);
      const j = i + delta;
      if (j < 0 || j >= o.length) return o;
      const copy = [...o];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
    setFocusTarget(`${id}:${delta < 0 ? "up" : "down"}`);
  };
  const check = () => {
    setChecked(true);
    if (order.every((id, i) => id === expected[i])) setCorrect((c) => c + 1);
  };
  const next = () => {
    if (last) {
      onFinish(correct, rounds.length);
      return;
    }
    const r = rounds[index + 1];
    setIndex(index + 1);
    setOrder(r.cards.map((c) => c.lead.id));
    setChecked(false);
  };

  return (
    <GameFrame
      step={t("gavion.training.game.round", {
        n: index + 1,
        total: rounds.length,
      })}
      current={index + (checked ? 1 : 0)}
      total={rounds.length}
      onQuit={onQuit}
    >
      <p className="text-sm">{t("gavion.training.order.instructions")}</p>
      <ol
        className="flex flex-col gap-2"
        aria-label={t("gavion.training.missions.read_queue.title")}
      >
        {order.map((id, i) => {
          const c = cardOf(id);
          const inPlace = checked && expected[i] === id;
          return (
            <li
              key={id}
              className={cn(
                "flex items-center gap-3 rounded-lg border bg-background p-3 motion-safe:transition-colors",
                checked &&
                  (inPlace
                    ? "border-green-600 bg-green-50 dark:bg-green-950"
                    : "border-destructive bg-destructive/10"),
              )}
            >
              <span
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-extrabold text-primary"
                aria-label={t("gavion.training.order.position", { n: i + 1 })}
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{c.name}</p>
                <p className="text-sm text-muted-foreground">
                  {t(c.labelKey, c.labelArgs)}
                </p>
              </div>
              {checked ? (
                inPlace ? (
                  <Check
                    className="size-5 shrink-0 text-green-700"
                    aria-hidden="true"
                  />
                ) : (
                  <X
                    className="size-5 shrink-0 text-destructive"
                    aria-hidden="true"
                  />
                )
              ) : (
                <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
                  <Button
                    ref={(el) => {
                      if (el) buttons.current.set(`${id}:up`, el);
                    }}
                    variant="outline"
                    size="icon"
                    className="size-9"
                    disabled={i === 0}
                    aria-label={t("gavion.training.order.move_up", {
                      name: c.name,
                    })}
                    onClick={() => move(id, -1)}
                  >
                    <ArrowUp className="size-4" aria-hidden="true" />
                  </Button>
                  <Button
                    ref={(el) => {
                      if (el) buttons.current.set(`${id}:down`, el);
                    }}
                    variant="outline"
                    size="icon"
                    className="size-9"
                    disabled={i === order.length - 1}
                    aria-label={t("gavion.training.order.move_down", {
                      name: c.name,
                    })}
                    onClick={() => move(id, 1)}
                  >
                    <ArrowDown className="size-4" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <div role="status" aria-live="polite">
        {checked && (
          <div className={cn("space-y-2", feedbackBox(right))}>
            <p className={feedbackTitle(right)}>
              {right ? (
                <Check className="size-4 shrink-0" aria-hidden="true" />
              ) : (
                <X className="size-4 shrink-0" aria-hidden="true" />
              )}
              {right
                ? t("gavion.training.order.right")
                : t("gavion.training.order.wrong")}
            </p>
            {!right && (
              <ol className="list-decimal pl-5">
                {expected.map((id) => (
                  <li key={id}>
                    <span className="font-semibold">{cardOf(id).name}</span> —{" "}
                    {t(cardOf(id).labelKey, cardOf(id).labelArgs)}
                  </li>
                ))}
              </ol>
            )}
            <p>{t(round.explanationKey, { hours: RETRY_GAP_HOURS })}</p>
          </div>
        )}
      </div>
      <details className="rounded-lg border px-3 py-2 text-sm">
        <summary className="cursor-pointer font-semibold">
          {t("gavion.training.order.rule_title")}
        </summary>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          {RULES.map((n) => (
            <li key={n}>
              {t(`gavion.training.order.rule.${n}`, { hours: RETRY_GAP_HOURS })}
            </li>
          ))}
        </ol>
      </details>
      {checked ? (
        <Button ref={nextRef} onClick={next} className="self-end">
          {last
            ? t("gavion.training.game.finish")
            : t("gavion.training.game.next")}
        </Button>
      ) : (
        <Button onClick={check} className="self-end">
          {t("gavion.training.order.check")}
        </Button>
      )}
    </GameFrame>
  );
};
